import React, { useState } from 'react'
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell,
} from 'recharts'
import * as XLSX from 'xlsx'
import { saveAs } from 'file-saver'
import DateRangeFilter, { type DateRangeOption } from '../../components/dashboard/DateRangeFilter'
import { useCostDashboard, type WorkflowTableRow } from '../../hooks/useCostDashboard'
import AgentDetailView from './AgentDetailView'
import WorkflowDetailView from './WorkflowDetailView'

// ────────────────────────────────────────────────────────────────────────────
// Helpers
// ────────────────────────────────────────────────────────────────────────────

function fmt(n: number): string {
  return n.toLocaleString(undefined, { maximumFractionDigits: 0 })
}

function fmtCost(n: number): string {
  if (n === 0) return '$0.00'
  if (n < 0.01) return `$${n.toFixed(6)}`
  return `$${n.toFixed(4)}`
}

// ────────────────────────────────────────────────────────────────────────────
// Sub-components
// ────────────────────────────────────────────────────────────────────────────

function MetricCard({
  label,
  value,
  sub,
  accent,
}: {
  label: string
  value: string
  sub?: string
  accent?: string
}) {
  return (
    <div className="bg-white rounded-xl border border-gray-200 p-5 flex flex-col gap-1 shadow-sm">
      <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">{label}</p>
      <p className={`text-2xl font-bold ${accent ?? 'text-gray-900'}`}>{value}</p>
      {sub && <p className="text-xs text-gray-400">{sub}</p>}
    </div>
  )
}

function SkeletonCard() {
  return (
    <div className="bg-white rounded-xl border border-gray-200 p-5 animate-pulse">
      <div className="h-3 w-24 bg-gray-200 rounded mb-3" />
      <div className="h-7 w-32 bg-gray-200 rounded mb-2" />
      <div className="h-3 w-16 bg-gray-200 rounded" />
    </div>
  )
}

const CHART_COLORS = ['#6366f1', '#10b981', '#f59e0b']

// ────────────────────────────────────────────────────────────────────────────
// Main page
// ────────────────────────────────────────────────────────────────────────────

const CostDashboardPage: React.FC = () => {
  const [selectedAgent, setSelectedAgent] = useState<string | null>(null)
  const [selectedWorkflow, setSelectedWorkflow] = useState<string | null>(null)
  const [dateRange, setDateRange] = useState<DateRangeOption>('last_7_days')
  const [customStart, setCustomStart] = useState<Date | undefined>()
  const [customEnd, setCustomEnd] = useState<Date | undefined>()

  const { data, loading, error } = useCostDashboard({
    dateRange,
    customStartDate: customStart,
    customEndDate: customEnd,
  })

  function handleDateChange(range: DateRangeOption, start?: Date, end?: Date) {
    setDateRange(range)
    setCustomStart(start)
    setCustomEnd(end)
  }

  function handleExport() {
    if (!data) return

    const wb = XLSX.utils.book_new()

    // ── Sheet 1: Summary Cards ───────────────────────────────────────────────
    const summaryRows = [
      ['Category', 'Value', 'Runs'],
      ['Resume Processing', fmtCost(data.resume_processing.total_cost_usd), data.resume_processing.runs],
      ['Job Analysis', fmtCost(data.job_analysis.total_cost_usd), data.job_analysis.runs],
      ['Job Matching', fmtCost(data.job_matching.total_cost_usd), data.job_matching.runs],
      [],
      ['Metric', 'Value'],
      ['Total Input Tokens', data.totals.total_input_tokens],
      ['Total Output Tokens', data.totals.total_output_tokens],
      ['Total Cost (All Agents)', fmtCost(data.totals.total_cost_usd)],
    ]
    XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(summaryRows), 'Summary')

    // ── Sheet 2: Activity Overview ───────────────────────────────────────────
    const activityRows = [
      ['Activity', 'Count'],
      ['Resume Submissions', data.activity.total_resumes_submitted],
      ['Job Submissions', data.activity.total_jobs_submitted],
      ['Interviews', data.activity.total_interviews],
    ]
    XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(activityRows), 'Activity Overview')

    // ── Sheet 3: Agent Breakdown ─────────────────────────────────────────────
    const agentHeader = ['Agent', 'Total Runs', 'Input Tokens', 'Output Tokens', 'Cached Tokens', 'Total Cost (USD)']
    const agentRows = data.agents.map((a) => [
      a.agent_name, a.runs, a.total_input_tokens, a.total_output_tokens, a.total_cached_tokens, a.total_cost_usd,
    ])
    const agentTotal = [
      'Total',
      data.agents.reduce((s, a) => s + a.runs, 0),
      data.agents.reduce((s, a) => s + a.total_input_tokens, 0),
      data.agents.reduce((s, a) => s + a.total_output_tokens, 0),
      data.agents.reduce((s, a) => s + a.total_cached_tokens, 0),
      data.agents.reduce((s, a) => s + a.total_cost_usd, 0),
    ]
    XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet([agentHeader, ...agentRows, agentTotal]), 'Agent Breakdown')

    // ── Sheet 4: Workflow Breakdown ──────────────────────────────────────────
    const wfHeader = ['Workflow', 'Total Runs', 'Input Tokens', 'Output Tokens', 'Cached Tokens', 'Total Cost (USD)']
    const wfRows = data.workflows.map((w: WorkflowTableRow) => [
      w.workflow_name, w.runs, w.total_input_tokens, w.total_output_tokens, w.total_cached_tokens, w.total_cost_usd,
    ])
    const wfTotal = [
      'Total',
      data.workflows.reduce((s, w) => s + w.runs, 0),
      data.workflows.reduce((s, w) => s + w.total_input_tokens, 0),
      data.workflows.reduce((s, w) => s + w.total_output_tokens, 0),
      data.workflows.reduce((s, w) => s + w.total_cached_tokens, 0),
      data.workflows.reduce((s, w) => s + w.total_cost_usd, 0),
    ]
    XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet([wfHeader, ...wfRows, wfTotal]), 'Workflow Breakdown')

    const buf = XLSX.write(wb, { bookType: 'xlsx', type: 'array' })
    saveAs(new Blob([buf], { type: 'application/octet-stream' }), `cost-dashboard-${dateRange}.xlsx`)
  }

  if (selectedAgent) {
    return <AgentDetailView agentName={selectedAgent} onBack={() => setSelectedAgent(null)} initialDateRange={dateRange} initialCustomStart={customStart} initialCustomEnd={customEnd} />
  }

  if (selectedWorkflow) {
    return <WorkflowDetailView workflowName={selectedWorkflow} onBack={() => setSelectedWorkflow(null)} initialDateRange={dateRange} initialCustomStart={customStart} initialCustomEnd={customEnd} />
  }

  const activityChartData = data
    ? [
        { name: 'Resume Submissions', count: data.activity.total_resumes_submitted },
        { name: 'Job Submissions', count: data.activity.total_jobs_submitted },
        { name: 'Interviews', count: data.activity.total_interviews },
      ]
    : []

  return (
    <div className="space-y-6">
      {/* Header + filter */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-xl font-semibold text-gray-900">Cost Dashboard</h2>
          <p className="text-sm text-gray-500 mt-0.5">AI agent usage, token consumption and associated costs</p>
        </div>
        <div className="flex items-center gap-2">
          <DateRangeFilter
            selectedRange={dateRange}
            customStartDate={customStart}
            customEndDate={customEnd}
            onChange={handleDateChange}
          />
          <button
            onClick={handleExport}
            disabled={!data || loading}
            className="flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed rounded-lg transition-colors"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M4 16v2a2 2 0 002 2h12a2 2 0 002-2v-2M7 10l5 5m0 0l5-5m-5 5V4" />
            </svg>
            Export to CSV
          </button>
        </div>
      </div>

      {error && (
        <div className="rounded-lg bg-red-50 border border-red-200 p-4 text-sm text-red-700">
          {error}
        </div>
      )}

      {/* ── Cost cards ─────────────────────────────────────────────────────── */}
      <div>
        <h3 className="text-sm font-semibold text-gray-700 mb-3 uppercase tracking-wide">Cost by Category</h3>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          {loading ? (
            <>
              <SkeletonCard /><SkeletonCard /><SkeletonCard />
            </>
          ) : (
            <>
              <MetricCard
                label="Resume Processing"
                value={fmtCost(data?.resume_processing.total_cost_usd ?? 0)}
                sub={`${fmt(data?.resume_processing.runs ?? 0)} runs`}
                accent="text-indigo-600"
              />
              <MetricCard
                label="Job Analysis"
                value={fmtCost(data?.job_analysis.total_cost_usd ?? 0)}
                sub={`${fmt(data?.job_analysis.runs ?? 0)} runs`}
                accent="text-emerald-600"
              />
              <MetricCard
                label="Job Matching"
                value={fmtCost(data?.job_matching.total_cost_usd ?? 0)}
                sub={`${fmt(data?.job_matching.runs ?? 0)} runs`}
                accent="text-amber-600"
              />
            </>
          )}
        </div>
      </div>

      {/* ── Token / total cost cards ────────────────────────────────────────── */}
      <div>
        <h3 className="text-sm font-semibold text-gray-700 mb-3 uppercase tracking-wide">Token Usage (All Configured Agents & Workflows)</h3>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          {loading ? (
            <>
              <SkeletonCard /><SkeletonCard /><SkeletonCard />
            </>
          ) : (
            <>
              <MetricCard
                label="Total Input Tokens"
                value={fmt(data?.totals.total_input_tokens ?? 0)}
              />
              <MetricCard
                label="Total Output Tokens"
                value={fmt(data?.totals.total_output_tokens ?? 0)}
              />
              <MetricCard
                label="Total Cost (All Agents)"
                value={fmtCost(data?.totals.total_cost_usd ?? 0)}
                accent="text-violet-600"
              />
            </>
          )}
        </div>
      </div>

      {/* ── Activity bar chart ──────────────────────────────────────────────── */}
      <div className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm">
        <h3 className="text-sm font-semibold text-gray-700 mb-4 uppercase tracking-wide">Activity Overview</h3>
        {loading ? (
          <div className="h-56 flex items-center justify-center">
            <div className="animate-pulse bg-gray-100 rounded w-full h-full" />
          </div>
        ) : (
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={activityChartData} barSize={48}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f0f0f0" />
              <XAxis dataKey="name" tick={{ fontSize: 12, fill: '#6b7280' }} axisLine={false} tickLine={false} />
              <YAxis allowDecimals={false} tick={{ fontSize: 12, fill: '#6b7280' }} axisLine={false} tickLine={false} />
              <Tooltip
                formatter={(value) => [fmt(value as number), 'Count']}
                contentStyle={{ borderRadius: 8, border: '1px solid #e5e7eb', fontSize: 13 }}
              />
              <Bar dataKey="count" radius={[4, 4, 0, 0]}>
                {activityChartData.map((_entry, index) => (
                  <Cell key={index} fill={CHART_COLORS[index % CHART_COLORS.length]} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        )}
      </div>

      {/* ── Agents table ────────────────────────────────────────────────────── */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
        <div className="px-5 py-4 border-b border-gray-100">
          <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">Agent Breakdown</h3>
        </div>
        {loading ? (
          <div className="p-5 space-y-3 animate-pulse">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="h-8 bg-gray-100 rounded" />
            ))}
          </div>
        ) : !data?.agents.length ? (
          <div className="p-8 text-center text-sm text-gray-400">No agent data for this period.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-5 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Agent</th>
                  <th className="px-5 py-3 text-right text-xs font-semibold text-gray-500 uppercase tracking-wide">Total Runs</th>
                  <th className="px-5 py-3 text-right text-xs font-semibold text-gray-500 uppercase tracking-wide">Input Tokens</th>
                  <th className="px-5 py-3 text-right text-xs font-semibold text-gray-500 uppercase tracking-wide">Output Tokens</th>
                  <th className="px-5 py-3 text-right text-xs font-semibold text-gray-500 uppercase tracking-wide">Cached Tokens</th>
                  <th className="px-5 py-3 text-right text-xs font-semibold text-gray-500 uppercase tracking-wide">Total Cost</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {data.agents.map((agent) => (
                  <tr key={agent.agent_name} className="hover:bg-gray-50 transition-colors">
                    <td className="px-5 py-3 font-mono text-xs">
                      <button
                        onClick={() => setSelectedAgent(agent.agent_name)}
                        className="text-indigo-600 hover:text-indigo-800 underline underline-offset-2 cursor-pointer text-left font-mono text-xs"
                      >
                        {agent.agent_name}
                      </button>
                    </td>
                    <td className="px-5 py-3 text-right text-gray-600">{fmt(agent.runs)}</td>
                    <td className="px-5 py-3 text-right text-gray-600">{fmt(agent.total_input_tokens)}</td>
                    <td className="px-5 py-3 text-right text-gray-600">{fmt(agent.total_output_tokens)}</td>
                    <td className="px-5 py-3 text-right text-gray-600">{fmt(agent.total_cached_tokens)}</td>
                    <td className="px-5 py-3 text-right font-semibold text-violet-700">{fmtCost(agent.total_cost_usd)}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot className="bg-gray-50 border-t border-gray-200">
                <tr>
                  <td className="px-5 py-3 font-semibold text-gray-700">Total</td>
                  <td className="px-5 py-3 text-right font-semibold text-gray-700">
                    {fmt(data.agents.reduce((s, a) => s + a.runs, 0))}
                  </td>
                  <td className="px-5 py-3 text-right font-semibold text-gray-700">
                    {fmt(data.agents.reduce((s, a) => s + a.total_input_tokens, 0))}
                  </td>
                  <td className="px-5 py-3 text-right font-semibold text-gray-700">
                    {fmt(data.agents.reduce((s, a) => s + a.total_output_tokens, 0))}
                  </td>
                  <td className="px-5 py-3 text-right font-semibold text-gray-700">
                    {fmt(data.agents.reduce((s, a) => s + a.total_cached_tokens, 0))}
                  </td>
                  <td className="px-5 py-3 text-right font-semibold text-violet-700">
                    {fmtCost(data.agents.reduce((s, a) => s + a.total_cost_usd, 0))}
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        )}
      </div>
      {/* ── Workflow Breakdown table ─────────────────────────────────────────── */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
        <div className="px-5 py-4 border-b border-gray-100">
          <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">Workflow Breakdown</h3>
        </div>
        {loading ? (
          <div className="p-5 space-y-3 animate-pulse">
            {[...Array(3)].map((_, i) => (
              <div key={i} className="h-8 bg-gray-100 rounded" />
            ))}
          </div>
        ) : !data?.workflows.length ? (
          <div className="p-8 text-center text-sm text-gray-400">No workflow data for this period.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-5 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Workflow</th>
                  <th className="px-5 py-3 text-right text-xs font-semibold text-gray-500 uppercase tracking-wide">Total Runs</th>
                  <th className="px-5 py-3 text-right text-xs font-semibold text-gray-500 uppercase tracking-wide">Input Tokens</th>
                  <th className="px-5 py-3 text-right text-xs font-semibold text-gray-500 uppercase tracking-wide">Output Tokens</th>
                  <th className="px-5 py-3 text-right text-xs font-semibold text-gray-500 uppercase tracking-wide">Cached Tokens</th>
                  <th className="px-5 py-3 text-right text-xs font-semibold text-gray-500 uppercase tracking-wide">Total Cost</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {data.workflows.map((wf: WorkflowTableRow) => (
                  <tr key={wf.workflow_name} className="hover:bg-gray-50 transition-colors">
                    <td className="px-5 py-3 font-mono text-xs">
                      <button
                        onClick={() => setSelectedWorkflow(wf.workflow_name)}
                        className="text-indigo-600 hover:text-indigo-800 underline underline-offset-2 cursor-pointer text-left font-mono text-xs"
                      >
                        {wf.workflow_name}
                      </button>
                    </td>
                    <td className="px-5 py-3 text-right text-gray-600">{fmt(wf.runs)}</td>
                    <td className="px-5 py-3 text-right text-gray-600">{fmt(wf.total_input_tokens)}</td>
                    <td className="px-5 py-3 text-right text-gray-600">{fmt(wf.total_output_tokens)}</td>
                    <td className="px-5 py-3 text-right text-gray-600">{fmt(wf.total_cached_tokens)}</td>
                    <td className="px-5 py-3 text-right font-semibold text-violet-700">{fmtCost(wf.total_cost_usd)}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot className="bg-gray-50 border-t border-gray-200">
                <tr>
                  <td className="px-5 py-3 font-semibold text-gray-700">Total</td>
                  <td className="px-5 py-3 text-right font-semibold text-gray-700">
                    {fmt(data.workflows.reduce((s, w) => s + w.runs, 0))}
                  </td>
                  <td className="px-5 py-3 text-right font-semibold text-gray-700">
                    {fmt(data.workflows.reduce((s, w) => s + w.total_input_tokens, 0))}
                  </td>
                  <td className="px-5 py-3 text-right font-semibold text-gray-700">
                    {fmt(data.workflows.reduce((s, w) => s + w.total_output_tokens, 0))}
                  </td>
                  <td className="px-5 py-3 text-right font-semibold text-gray-700">
                    {fmt(data.workflows.reduce((s, w) => s + w.total_cached_tokens, 0))}
                  </td>
                  <td className="px-5 py-3 text-right font-semibold text-violet-700">
                    {fmtCost(data.workflows.reduce((s, w) => s + w.total_cost_usd, 0))}
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        )}
      </div>
    </div>
  )
}

export default CostDashboardPage
